import { useCallback, useEffect, useState } from 'react';
import apiService from '../../api/apiService';
import { RecipeRealization } from '../../api/Types';
import { supabaseService } from '../../services/SupabaseClient';

const sortByNewest = (items: RecipeRealization[]): RecipeRealization[] => {
  return [...items].sort((left, right) => right.created_at.localeCompare(left.created_at));
};

interface UseRecipeRealizationsFeedParams {
  recipeId: string;
  authorId: number | null | undefined;
  authLoading: boolean;
}

interface UseRecipeRealizationsFeedResult {
  realizations: RecipeRealization[];
  loading: boolean;
  error: string;
  setError: (error: string) => void;
  reload: () => Promise<void>;
}

export const useRecipeRealizationsFeed = ({
  recipeId,
  authorId,
  authLoading,
}: UseRecipeRealizationsFeedParams): UseRecipeRealizationsFeedResult => {
  const [realizations, setRealizations] = useState<RecipeRealization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!authorId) {
      setRealizations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const nextRealizations = await apiService.listRecipeRealizations(recipeId);
      setRealizations(sortByNewest(nextRealizations));
    } catch (err: any) {
      setError(err?.message || 'Impossible de charger les realisations.');
    } finally {
      setLoading(false);
    }
  }, [authorId, recipeId]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void reload();
  }, [authLoading, reload]);

  useEffect(() => {
    let isCancelled = false;
    let cleanup: (() => void) | null = null;

    if (!authorId) {
      return () => {
        isCancelled = true;
      };
    }

    const subscribeToRealtime = async () => {
      try {
        const realtimeClient = await supabaseService.getClient();
        if (isCancelled) {
          return;
        }

        const channel = realtimeClient
          .channel(`recipe_realizations_${recipeId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'recipe_realizations',
              filter: `recipe_id=eq.${recipeId}`,
            },
            () => {
              void reload();
            }
          )
          .subscribe();

        cleanup = () => {
          void realtimeClient.removeChannel(channel);
        };
      } catch (err) {
        console.error('Failed to subscribe to recipe realizations:', err);
      }
    };

    void subscribeToRealtime();

    return () => {
      isCancelled = true;
      if (cleanup) {
        cleanup();
      }
    };
  }, [authorId, recipeId, reload]);

  return {
    realizations,
    loading,
    error,
    setError,
    reload,
  };
};

export default useRecipeRealizationsFeed;
